import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { catchError, defer, first, Observable, of, switchMap, throwError } from 'rxjs';
import { TokenStoreService } from '@services/token-store.service';
import { AuthService } from '@services/auth.service';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/toast.service';
import { JwtService } from '@services/jwt.service';
import { OnlineStateService } from '@services/online-state.service';
import { ConfigService } from '@services/config.service';
import { PlatformService } from '@services/platform.service';
import { AppNavigationService } from '@state-routing';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly appNavigation = inject(AppNavigationService);
  private readonly tokenStore = inject(TokenStoreService);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly jwtService = inject(JwtService);
  private readonly onlineStateService = inject(OnlineStateService);
  private readonly configService = inject(ConfigService);
  private readonly platformService = inject(PlatformService);

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.url.includes(this.configService.urlBackEndApi)
      || request.url.includes('/auth/refresh')) {
      return next.handle(request);
    }

    return defer(() => {
      let sessionVersion = this.authService.sessionVersion;
      const waitForNetwork = !this.platformService.isSSR && this.onlineStateService.isOffline;
      if (waitForNetwork) {
        console.warn('No internet connection, request will be delayed until online');
      }
      const online$ = waitForNetwork
        ? this.onlineStateService.isOffline$.pipe(first(isOffline => !isOffline))
        : of(false);

      return online$.pipe(
        switchMap(() => {
          sessionVersion = this.authService.sessionVersion;
          return this.refreshExpiredToken(sessionVersion);
        }),
        switchMap(() => this.sendAuthorizedRequest(request, next, sessionVersion).pipe(
          catchError(error => {
            if (sessionVersion !== this.authService.sessionVersion) return throwError(() => error);
            if (error?.status !== HttpStatusCode.Unauthorized || !this.authService.isAuthenticated) {
              return this.failRequest(error, false, sessionVersion);
            }

            // Refresh is shared by AuthService. Unsubscribing stops only this request's continuation.
            return defer(() => this.authService.refreshToken()).pipe(
              catchError(refreshError => {
                if (refreshError?.status === HttpStatusCode.Unauthorized) {
                  console.error('Retry refresh token failed, logout...');
                }
                return this.failRequest(refreshError, true, sessionVersion);
              }),
              switchMap(() => this.sendAuthorizedRequest(request, next, sessionVersion).pipe(
                catchError(retryError => {
                  if (retryError?.status === HttpStatusCode.Unauthorized) {
                    console.error('Retry request failed, logout...');
                  }
                  return this.failRequest(retryError, true, sessionVersion);
                }),
              )),
            );
          }),
        )),
      );
    });
  }

  private refreshExpiredToken(sessionVersion: number): Observable<void> {
    return defer(() => this.tokenStore.getToken()).pipe(
      switchMap(jwtToken => {
        if (sessionVersion !== this.authService.sessionVersion) return throwError(() => new Error('Session changed'));
        if (!this.authService.isAuthenticated || (jwtToken && !this.jwtService.checkIsTokenExpired(jwtToken))) return of(undefined);

        console.warn('interceptor: Token expired!');
        return defer(() => this.authService.refreshToken()).pipe(
          // Preserve the existing fallback: a non-401 refresh failure still allows the original request.
          catchError(error => {
            if (error?.status === HttpStatusCode.Unauthorized) {
              console.error('First refresh token failed, logout...');
              return this.failRequest(error, true, sessionVersion);
            }
            return of(undefined);
          }),
        );
      }),
    );
  }

  private sendAuthorizedRequest(request: HttpRequest<any>, next: HttpHandler, sessionVersion: number): Observable<HttpEvent<any>> {
    return defer(() => this.patchRequestIfTokenAvailable(request)).pipe(
      switchMap(patchedRequest => sessionVersion === this.authService.sessionVersion
        ? next.handle(patchedRequest)
        : throwError(() => new Error('Session changed'))),
    );
  }

  private failRequest(error: any, logoutOnUnauthorized = false, sessionVersion = this.authService.sessionVersion): Observable<never> {
    return defer(async (): Promise<never> => {
      if (sessionVersion !== this.authService.sessionVersion) throw error;
      if (logoutOnUnauthorized && error?.status === HttpStatusCode.Unauthorized) {
        const responseMessage: string = error?.result?.error ?? error?.result?.message;
        await this.logout(responseMessage, sessionVersion);
      }
      await this.handleHttpException(error);
      throw error;
    });
  }

  private async patchRequestIfTokenAvailable(request: HttpRequest<any>): Promise<HttpRequest<any>> {
    const jwtToken: string = await this.tokenStore.getToken();

    if (jwtToken && this.authService.isAuthenticated) {
      return request.clone({headers: request.headers.set('Authorization', `Bearer ${jwtToken}`)});
    } else {
      const tempToken: string = this.tokenStore.getTempToken();
      return request.clone({headers: request.headers.set('baggage', tempToken)});
    }
  }

  private async handleHttpException(error: any): Promise<void> {
    const responseMessage: string = error?.result?.error ?? error?.result?.message;

    if (this.platformService.isSSR) {
      console.error(responseMessage || error?.message);
      return;
    }

    if (responseMessage) {
      const codesForRedirect: string[] = ['ban.task', 'ban.reply', 'ban.forever'];

      if (error.status == HttpStatusCode.Forbidden && codesForRedirect.includes(responseMessage)) {
        await this.toastService.error(this.translate.instant('errors.' + responseMessage)); // 'ban.task', 'ban.reply', 'ban.forever'
        await this.appNavigation.redirect('/avatar');
      } else if (!environment.production) {
        await this.toastService.error(responseMessage);
      }
    } else {
      console.error(error.message);
    }
  }

  private async logout(message: string, sessionVersion: number): Promise<void> {
    const translate: string = this.translate.instant(message);
    if (this.platformService.isSSR) {
      console.error(translate);
      return;
    }

    console.error(translate);
    await this.toastService.warning(translate);
    if (sessionVersion === this.authService.sessionVersion) await this.authService.logout();
  }
}
